import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { signOut } from "firebase/auth";
import { auth } from "../contexts/firebase";
import { motion, AnimatePresence } from "framer-motion";
import { UserCircleIcon } from "@heroicons/react/outline";

export default function Navbar() {
  const { currentUser, credits } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef();

  useEffect(() => {
    const handleClickOutside = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    if (auth.currentUser) {
      await signOut(auth);
    }
    navigate("/login");
  };

  return (
    <motion.nav
      className="fixed top-0 left-0 w-full bg-black/60 backdrop-blur-md text-white z-50"
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="GeneArtAI Logo" className="object-contain scale-125" />
          </div>
          <Link to="/" className="text-2xl font-extrabold">GeneArtAI</Link>
        </div>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">

          {currentUser ? (
            <>
              <Link to="/dashboard" className="hover:text-gray-300 transition">Dashboard</Link>
              <Link to="/generate" className="hover:text-gray-300 transition">Generate</Link>
              <Link to="/contact">ContactUs</Link>

              {currentUser && typeof credits === 'number' && (
                <motion.button
                  key={credits}
                  onClick={() => navigate("/dashboard")}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 150, damping: 20, mass: 0.5 }}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 font-bold px-3 py-1 rounded-full text-xs md:text-sm shadow-md hover:scale-105 transition"
                >
                  🪙 {credits} Credits
                </motion.button>
              )}

              {/* User Icon */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="flex items-center justify-center h-8 w-8 text-white hover:text-gray-300"
                >
                  <UserCircleIcon className="h-7 w-7" />
                </button>
                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.ul
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-40 bg-black/50 backdrop-blur-md text-gray-100 rounded-lg shadow-lg overflow-hidden"
                    >
                      <li>
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate("/profile"); }}
                          className="block w-full text-left px-4 py-2 hover:bg-black/60"
                        >
                          Profile
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                          className="block w-full text-left px-4 py-2 hover:bg-black/60"
                        >
                          Logout
                        </button>
                      </li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="hover:text-gray-300 transition">Login/Signup</Link>
              {/* <Link to="/signup" className="hover:text-gray-300 transition">Sign Up</Link> */}
              <Link to="/" className="hover:text-gray-300 transition">Home</Link>
              <Link to="/about">About</Link>
              <Link to="/faqs">FAQs</Link>
              <Link to="/contact">ContactUs</Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden focus:outline-none"
          onClick={() => setIsOpen(o => !o)}
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="md:hidden flex flex-col items-center gap-4 bg-black/80 backdrop-blur-md py-4"
          >
            {currentUser ? (
              <>
                <Link to="/dashboard" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">
                  Dashboard
                </Link>
                <Link to="/generate" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">
                  Generate
                </Link>
                <Link to="/profile" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">
                  Profile
                </Link>
                <Link to="/contact" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">ContactUs</Link>
                {currentUser && typeof credits === 'number' && (
                  <div className="text-yellow-400 font-semibold">
                    🪙 {credits} Credits
                  </div>
                )}
                <button
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  className="bg-white text-[#166CB0] px-6 py-2 rounded-full hover:bg-gray-100 transition text-sm font-semibold"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">
                  Login/Signup
                </Link>
                <Link to="/" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">Home</Link>
                <Link to="/about" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">About</Link>
                <Link to="/faqs" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">FAQs</Link>
                <Link to="/contact" onClick={() => setIsOpen(false)} className="hover:text-gray-300 transition">Contact</Link>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
